import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable, shareReplay } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { API_ENDPOINTS } from '@app/core/constants/api-endpoints';

export interface CurrencyRecord {
  id: string;
  name: string;
  code: string;
  symbol: string;
  isActive: boolean;
  isDefault: boolean;
  archived: boolean;
  createdAt?: string;
}

export interface CurrencySelection {
  id: string;
  code: string;
  symbol: string;
}

export const USD_FALLBACK_SELECTION: CurrencySelection = {
  id: '',
  code: 'USD',
  symbol: '$',
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** True only for workspace currency row ids (never synthetic ng-select placeholders). */
export function isWorkspaceCurrencyId(id: string | null | undefined): boolean {
  const t = (id ?? '').trim();
  return !!t && UUID_RE.test(t);
}

/** ARR payload fields — omit currencyId unless it is a valid workspace UUID. */
export function arrCurrencyFieldsForApi(selection: CurrencySelection): {
  currencyCode: string;
  currencyId?: string;
  currencySymbol?: string;
} {
  const currencyCode =
    (selection.code ?? 'USD').trim().toUpperCase() || 'USD';
  const out: {
    currencyCode: string;
    currencyId?: string;
    currencySymbol?: string;
  } = { currencyCode };
  if (isWorkspaceCurrencyId(selection.id)) {
    out.currencyId = selection.id.trim();
  }
  const sym = (selection.symbol ?? '').trim();
  if (sym) {
    out.currencySymbol = sym;
  }
  return out;
}

function asBool(value: unknown, fallback = false): boolean {
  if (value === true || value === 'true' || value === 1 || value === '1') return true;
  if (value === false || value === 'false' || value === 0 || value === '0') return false;
  return fallback;
}

function normalizeCurrency(rec: Record<string, unknown>): CurrencyRecord | null {
  const id = String(rec['id'] ?? '').trim();
  if (!id) return null;
  const name = String(rec['name'] ?? '').trim() || 'Currency';
  const code = String(rec['code'] ?? '').trim().toUpperCase();
  const symbol = String(rec['icon'] ?? rec['symbol'] ?? '').trim();
  const createdAt = String(rec['createdAt'] ?? rec['createdDate'] ?? '').trim();
  const archived = asBool(rec['archived'] ?? rec['isArchived'], false);
  const isActive = asBool(rec['isActive'], true);
  const isDefault = asBool(
    rec['isDefault'] ??
      rec['default'] ??
      rec['isWorkspaceDefault'] ??
      rec['defaultCurrency'] ??
      rec['isDefaultCurrency'],
    false,
  );

  return {
    id,
    name,
    code,
    symbol,
    isActive,
    isDefault,
    archived,
    ...(createdAt ? { createdAt } : {}),
  };
}

/**
 * Settings list: workspace default first; preserve API order for all other rows.
 */
export function sortCurrenciesForListDisplay(
  list: readonly CurrencyRecord[],
): CurrencyRecord[] {
  return list
    .map((c, index) => ({ c, index }))
    .sort((a, b) => {
      if (a.c.isDefault !== b.c.isDefault) {
        return a.c.isDefault ? -1 : 1;
      }
      return a.index - b.index;
    })
    .map(({ c }) => c);
}

function unwrapList(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    const data = (raw as Record<string, unknown>)['data'];
    if (Array.isArray(data)) return data;
  }
  return [];
}

export function resolveCurrencySymbol(
  code: string | undefined,
  storedSymbol?: string | null,
  currencies?: readonly CurrencyRecord[],
): string {
  const sym = (storedSymbol ?? '').trim();
  if (sym) return sym;
  const c = (code ?? 'USD').trim().toUpperCase() || 'USD';
  if (currencies?.length) {
    const match = currencies.find((x) => x.code === c);
    if (match?.symbol) return match.symbol;
  }
  try {
    const parts = new Intl.NumberFormat('en', {
      style: 'currency',
      currency: c,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value ?? c;
  } catch {
    return c;
  }
}

export function selectionFromCode(
  code: string,
  currencies?: readonly CurrencyRecord[],
  storedSymbol?: string | null,
  storedId?: string | null,
): CurrencySelection {
  const normalized = (code || 'USD').trim().toUpperCase() || 'USD';
  if (currencies?.length) {
    const match = currencies.find((x) => x.code === normalized);
    if (match) {
      return {
        id: match.id,
        code: match.code,
        symbol: (match.symbol || match.code).trim(),
      };
    }
  }
  return {
    id: (storedId ?? '').trim(),
    code: normalized,
    symbol: resolveCurrencySymbol(normalized, storedSymbol, currencies),
  };
}

function readDefaultCurrencyFromStorage(): CurrencySelection | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem('defaultCurrency');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { id?: string; code?: string; symbol?: string; icon?: string };
    const code =
      typeof parsed?.code === 'string' && parsed.code.trim()
        ? parsed.code.trim().toUpperCase()
        : '';
    if (!code) return null;
    const symbol =
      (typeof parsed.symbol === 'string' && parsed.symbol.trim()) ||
      (typeof parsed.icon === 'string' && parsed.icon.trim()) ||
      code;
    return {
      id: typeof parsed.id === 'string' ? parsed.id.trim() : '',
      code,
      symbol,
    };
  } catch {
    return null;
  }
}

export function resolveDefaultCurrencySelection(
  currencies: readonly CurrencyRecord[],
): CurrencySelection {
  const active = currencies.filter((c) => c.isActive && !c.archived);
  const fromApi = active.find((c) => c.isDefault);
  if (fromApi) {
    return { id: fromApi.id, code: fromApi.code, symbol: fromApi.symbol || fromApi.code };
  }
  const fromStorage = readDefaultCurrencyFromStorage();
  if (fromStorage) {
    const match = active.find((c) => c.code === fromStorage.code);
    if (match) {
      return { id: match.id, code: match.code, symbol: match.symbol || match.code };
    }
    return fromStorage;
  }
  const firstActive = active[0];
  if (firstActive) {
    return { id: firstActive.id, code: firstActive.code, symbol: firstActive.symbol || firstActive.code };
  }
  return { ...USD_FALLBACK_SELECTION };
}

@Injectable({ providedIn: 'root' })
export class CurrencyService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;
  private currenciesCache$?: Observable<CurrencyRecord[]>;

  getCurrencies(): Observable<CurrencyRecord[]> {
    if (!this.currenciesCache$) {
      this.currenciesCache$ = this.http
        .get<unknown>(`${this.apiUrl}/${API_ENDPOINTS.CURRENCY}`)
        .pipe(
          map((raw) =>
            sortCurrenciesForListDisplay(
              unwrapList(raw)
                .filter((x): x is Record<string, unknown> => !!x && typeof x === 'object')
                .map((x) => normalizeCurrency(x))
                .filter((x): x is CurrencyRecord => !!x),
            ),
          ),
          shareReplay(1),
        );
    }
    return this.currenciesCache$;
  }

  getActiveCurrencies(): Observable<CurrencyRecord[]> {
    return this.getCurrencies().pipe(
      map((list) => list.filter((c) => c.isActive && !c.archived)),
    );
  }

  getDefaultCurrency(): Observable<CurrencySelection> {
    return this.getCurrencies().pipe(map((list) => resolveDefaultCurrencySelection(list)));
  }

  createCurrency(payload: Record<string, unknown>): Observable<unknown> {
    this.invalidateCache();
    return this.http.post(`${this.apiUrl}/${API_ENDPOINTS.CURRENCY}`, payload);
  }

  updateCurrency(id: string, payload: Record<string, unknown>): Observable<unknown> {
    this.invalidateCache();
    return this.http.patch(`${this.apiUrl}/${API_ENDPOINTS.CURRENCY}/${encodeURIComponent(id)}`, payload);
  }

  deleteCurrency(id: string): Observable<unknown> {
    this.invalidateCache();
    return this.http.delete(`${this.apiUrl}/${API_ENDPOINTS.CURRENCY}/${encodeURIComponent(id)}`);
  }

  invalidateCache(): void {
    this.currenciesCache$ = undefined;
  }
}
